import { Check, Plus, X } from 'lucide-react';
import { type ReactElement, useCallback } from 'react';

import { Button } from '@/ux-common/components/button/button';

import type { MultiSelectFilterProps } from '../types';

/**
 * Multi-select filter with checkbox pattern.
 * Matches the old data-table filter UI.
 */
export const MultiSelectFilter = ({
  value,
  onChange,
  options,
  onClear,
  isActive,
  onClose,
}: MultiSelectFilterProps & { isActive: boolean; onClose: () => void }): ReactElement => {
  const toggleItem = useCallback(
    (item: string) => {
      if (value.includes(item)) {
        // Remove item
        const newValue = value.filter((i) => i !== item);
        onChange(newValue);
      } else {
        // Add item
        onChange([...value, item]);
      }
    },
    [value, onChange]
  );

  const handleRemoveItem = useCallback(
    (e: React.MouseEvent, item: string) => {
      e.stopPropagation();
      const newValue = value.filter((i) => i !== item);
      onChange(newValue);
    },
    [value, onChange]
  );

  // Don't render if no options
  if (!options || options.length === 0) {
    return <div className="text-sm text-muted-foreground p-2">No filter options available</div>;
  }

  return (
    <>
      <div className="max-h-96 overflow-y-auto">
        {/* Selected items section */}
        {value.length > 0 && (
          <>
            {value
              .sort((a, b) => a.localeCompare(b))
              .map((item) => (
                <div
                  key={item}
                  className="flex items-center justify-between cursor-pointer bg-accent/50 p-2 hover:bg-accent/70"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleItem(item);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Check className="glyph glyph-sm text-foreground" aria-hidden="true" />
                    <span className="truncate font-semibold normal-case text-sm">{item}</span>
                  </div>
                  <X
                    className="glyph glyph-sm cursor-pointer text-foreground/50 hover:text-foreground"
                    onClick={(e) => handleRemoveItem(e, item)}
                  />
                </div>
              ))}
          </>
        )}

        {/* Available items section */}
        {options
          .filter((item) => !value.includes(item))
          .sort((a, b) => a.localeCompare(b))
          .map((item) => (
            <div
              key={item}
              className="flex items-center cursor-pointer p-2 hover:bg-accent/50"
              onClick={(e) => {
                e.stopPropagation();
                toggleItem(item);
              }}
            >
              <div className="flex items-center gap-2">
                <Plus className="glyph glyph-sm text-foreground" aria-hidden="true" />
                <span className="truncate font-semibold normal-case text-sm">{item}</span>
              </div>
            </div>
          ))}
      </div>

      {/* Footer with Clear/Close buttons */}
      <div className="flex justify-end p-3 border-t border-border">
        {isActive && (
          <Button
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onClear();
            }}
            className="btn btn-tier3 btn-sm mr-2 font-semibold"
          >
            Clear
          </Button>
        )}
        <Button onClick={onClose} className="btn btn-tier2 btn-sm font-semibold">
          Close
        </Button>
      </div>
    </>
  );
};

MultiSelectFilter.displayName = 'MultiSelectFilter';
